import React from 'react';
import clsx from 'clsx';

import { FlagKey } from '../types/Flag';
import { LoggerListItem } from '../types/Logger';
import { ModalContent } from '../types/Modal';
import { extractLoggerFlags } from './FlagHelpers';

export const ServiceCategory = {
    END_OF_LIFE: 'endOfLife',
    BATTERY_REPLACEMENT: 'batteryReplacement',
    PROBE_ISSUES: 'probeIssues',
    OTHER_SERVICE_ISSUES: 'otherServiceIssues',
    NO_SERVICE_NEEDED: 'noServiceNeeded'
} as const;

export type ServiceCategory = (typeof ServiceCategory)[keyof typeof ServiceCategory];

interface StatisticsResult {
    totalLoggers: number;
    categories: Record<ServiceCategory, number>;
}

export interface CustomerStatistics {
    customerName: string;
    customerId: string;
    stats: StatisticsResult;
}

const FLAG_GROUPS = {
    endOfLife: ['service.endOfLife', 'serviceSoft.endOfLife'] as FlagKey[],
    batteryReplacement: ['service.batteryReplacement', 'serviceSoft.batteryReplacement'] as FlagKey[],
    probeIssues: ['service.probeReplacement', 'serviceSoft:probeReplacement', 'service.probeReinforcement'] as FlagKey[]
} as const;

/**
 * Categorizes a logger based on its flags and calibration status.
 * Returns the most severe category applicable (highest to lowest priority):
 * endOfLife > batteryReplacement > probeIssues > otherServiceIssues > noServiceNeeded
 *
 * otherServiceIssues is a catch-all for any other service flags or calibration check failures
 *
 * @param loggerFlags - Array of flag keys associated with the logger
 * @param isFailingCalibration - Whether the logger is failing calibration checks
 * @returns The service category for the logger
 */
const categorizeLogger = (loggerFlags: FlagKey[], isFailingCalibration: boolean): ServiceCategory => {
    // Check in severity order
    if (loggerFlags.some((key) => FLAG_GROUPS.endOfLife.includes(key))) {
        return ServiceCategory.END_OF_LIFE;
    }
    if (loggerFlags.some((key) => FLAG_GROUPS.batteryReplacement.includes(key))) {
        return ServiceCategory.BATTERY_REPLACEMENT;
    }
    if (loggerFlags.some((key) => FLAG_GROUPS.probeIssues.includes(key))) {
        return ServiceCategory.PROBE_ISSUES;
    }

    const hasServiceFlag = loggerFlags.some((key) => key.startsWith('service.') || key.startsWith('serviceSoft.'));

    if (hasServiceFlag || isFailingCalibration) {
        return ServiceCategory.OTHER_SERVICE_ISSUES;
    }

    return ServiceCategory.NO_SERVICE_NEEDED;
};

export const generateScanStatistics = (loggerList: Array<LoggerListItem>): Map<string, CustomerStatistics> => {
    const customerStatsMap = new Map<string, CustomerStatistics>();

    loggerList.forEach((logger) => {
        const customerId = logger.customer.customerId?.toString() ?? 'unknown';
        const customerName = logger.customer.name || 'Unknown Customer';

        let customerData = customerStatsMap.get(customerId);

        // Initialize customer stats if it does not exist
        if (!customerData) {
            customerData = {
                customerName,
                customerId,
                stats: {
                    totalLoggers: 0,
                    categories: {
                        noServiceNeeded: 0,
                        endOfLife: 0,
                        batteryReplacement: 0,
                        probeIssues: 0,
                        otherServiceIssues: 0
                    }
                }
            };
            customerStatsMap.set(customerId, customerData);
        }

        const customerStats = customerData.stats;

        customerStats.totalLoggers++;

        const loggerFlagKeys = extractLoggerFlags(logger);
        const isFailingCalibration = logger.status?.calibration?.status === 'failed';
        const category = categorizeLogger(loggerFlagKeys, isFailingCalibration);

        customerStats.categories[category]++;
    });

    return customerStatsMap;
};

export const SERVICE_CATEGORIES: Array<{ key: ServiceCategory; label: string; variant: 'success' | 'warning' }> = [
    { key: ServiceCategory.NO_SERVICE_NEEDED, label: 'No Service Needed', variant: 'success' },
    { key: ServiceCategory.END_OF_LIFE, label: 'End of Life', variant: 'warning' },
    { key: ServiceCategory.BATTERY_REPLACEMENT, label: 'Battery Replacement', variant: 'warning' },
    { key: ServiceCategory.PROBE_ISSUES, label: 'Probe Issues', variant: 'warning' },
    {
        key: ServiceCategory.OTHER_SERVICE_ISSUES,
        label: 'Other Service Flags or Calibration Needed',
        variant: 'warning'
    }
];

export const getStatisticsModalContent = (loggerList: Array<LoggerListItem>, isBox: boolean): ModalContent => {
    const customerStatsMap = generateScanStatistics(loggerList);

    return {
        type: 'INFORMATION',
        header: `Summary`,
        body: (
            <div className="statistics-content">
                <div className="summary-info-text">
                    This summary shows totals for the entire {isBox ? 'box' : 'scan'}: the total number of loggers and
                    total number of customers.
                </div>

                <div className="customer-stats-section" data-testid="general-summary">
                    <h3>General Summary</h3>
                    <div className="stats-section">
                        <StatRow label="Total Loggers" value={loggerList.length} />
                        <StatRow label="Total Customers" value={customerStatsMap.size} />
                    </div>
                </div>

                {customerStatsMap.size > 0 && (
                    <>
                        <div className="summary-info-text">
                            Logger counts grouped by customer. Each logger appears in only one category based on its
                            most severe issue (top to bottom). Loggers without service issues are counted as 'No Service
                            Needed'.
                        </div>

                        {Array.from(customerStatsMap.values()).map((customerData) => (
                            <section
                                key={customerData.customerId}
                                className="customer-stats-section"
                                aria-labelledby={`customer-${customerData.customerId}`}
                            >
                                <h3 id={`customer-${customerData.customerId}`}>{customerData.customerName}</h3>

                                <div className="stats-section">
                                    {SERVICE_CATEGORIES.map(({ key, label, variant }) => {
                                        const value = customerData.stats.categories[key];

                                        return <StatRow key={key} label={label} value={value} variant={variant} />;
                                    })}
                                </div>

                                <div className="stats-section">
                                    <div className="stat-row">
                                        <span>Total Loggers</span>
                                        <span className="stat-badge">{customerData.stats.totalLoggers}</span>
                                    </div>
                                </div>
                            </section>
                        ))}
                    </>
                )}
            </div>
        )
    };
};

const StatRow = ({ label, value, variant }: { label: string; value: number; variant?: 'success' | 'warning' }) => (
    <div className="stat-row">
        <span>{label}</span>
        <span
            data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className={clsx('stat-badge', variant && `stat-badge-${variant}`)}
        >
            {value}
        </span>
    </div>
);
